import { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { getUserAccountTypeIsInternal, getUserRole } from 'AuthPackage/MainModule/selectors/authSelectors';
import { num2str } from 'CorePackage/MainModule/common/num2str';
import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { ImportFileStatusEnum } from 'APIPackage/ImportExportModule/enums/ImportFileStatusEnum';
import { UserRoleEnum } from 'APIPackage/AuthModule/enums/UserRoleEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { getSortOrder } from 'CorePackage/MainModule/common/getSortOrder';
import { IImportTemplate } from 'APIPackage/ImportExportModule/interfaces/IImportTemplate';
import { handleSortChange } from 'CorePackage/MainModule/common/handleSortChange';
import { Table } from 'CorePackage/TableModule/components/Table';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { TablePage } from 'CorePackage/TableModule/components/TablePage';
import { IUserRole } from 'APIPackage/AuthModule/interfaces/IUserRole';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { ImportPanel } from './ImportPanel';
import columnStyles from './ImportTable.module.scss';
import { importFileTypesExec } from '../constants/importFileTypesExec';
import { importFileTypesProc } from '../constants/importFileTypesProc';
import { importFileStatuses } from '../constants/importFileStatuses';
import { initialTableColumns } from '../constants/initialTableColumns';
import { importFileTypesExecErknm } from '../constants/importFileTypesExecErknm';
import { importFileTypesProcErknm } from '../constants/importFileTypesProcErknm';
import {
  importGetData,
  importGetDataNextPage,
  importInitPage,
  importRemove,
  importSetFilters,
  importSetOrder,
} from '../actions/importActions';
import { getImportData, getImportTotalCount } from '../selectors/importSelectors';
import { IImportFilters } from '../interfaces/IImportFilters';
import { IImportRow } from '../interfaces/IImportRow';
import { ImportDropdown } from './ImportDropdown';
import { importFileTypesTestExecErknm } from '../constants/importFileTypesTestExecErknm';

export type IItemsCheck = Record<string, boolean>;

const rowClassNames = (row: IImportRow) => {
  const { fileStatus } = row;
  const fileStatusCode = importFileStatuses.find(status => status.name === fileStatus)?.id;

  return {
    ...(fileStatusCode === ImportFileStatusEnum.NEW && { 'data-info': true }),
    ...((fileStatusCode === ImportFileStatusEnum.PROCESSING ||
      fileStatusCode === ImportFileStatusEnum.PROCESSING_PAUSED) && { 'data-default': true }),
    ...(fileStatusCode === ImportFileStatusEnum.CREATED_SUCCESS && { 'data-done': true }),
    ...((fileStatusCode === ImportFileStatusEnum.CREATED_WARNINGS ||
      fileStatusCode === ImportFileStatusEnum.CREATED_PARTIALLY) && { 'data-warning': true }),
    ...((fileStatusCode === ImportFileStatusEnum.PROCESSING_FAILED ||
      fileStatusCode === ImportFileStatusEnum.NOT_CREATED ||
      fileStatusCode === ImportFileStatusEnum.IMPOSSIBLE_TO_CORRECT) && { 'data-error': true }),
  };
};

interface IImportStoreProps {
  role: IUserRole | undefined;
  isErknm: boolean;
  totalCount: number;
  importTemplates: IImportRow[];
  isInternalAccount: boolean;
}

const ImportWithoutStore = ({ importTemplates, isErknm, role, totalCount, isInternalAccount }: IImportStoreProps) => {
  const [deletingTemplatesIds, setDeletingTemplatesIds] = useState<Array<number> | undefined>();

  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    const order = getSortOrder(initialTableColumns);

    dispatch(importSetOrder(order));
  }, [dispatch]);

  useEffect(() => {
    dispatch(importInitPage());
  }, [dispatch]);

  const [tableColumns, setTableColumns] = useState<ITableColumn<IImportRow>[]>(initialTableColumns);
  const [itemsCheck, setItemsCheck] = useState<TableCheckItems>({});
  const [isDeleteButtonEnabled, setIsDeleteButtonEnabled] = useState(false);

  useEffect(() => {
    if (isErknm) {
      setTableColumns(initialTableColumns.filter(item => item.id !== 'federalLaw'));
    } else {
      setTableColumns(initialTableColumns);
    }
  }, [isErknm]);

  const handleFiltersApply = useCallback(
    (newFilters: IImportFilters) => {
      dispatch(importSetFilters(newFilters));
      dispatch(importGetData());
    },
    [dispatch],
  );

  const handleImportPanelDelete = useCallback(() => {
    const deletingIds = Object.keys(itemsCheck)
      .filter(key => itemsCheck[key])
      .map(key => Number(key));

    setDeletingTemplatesIds(deletingIds);
  }, [itemsCheck]);

  const handleImportTableRowDelete = useCallback((id: number) => setDeletingTemplatesIds([id]), []);

  useEffect(() => {
    const keysArray = Object.keys(itemsCheck);
    const isAnyItemTrue = keysArray.some(key => itemsCheck[key]);

    setIsDeleteButtonEnabled(isAnyItemTrue);
  }, [itemsCheck]);

  useEffect(() => {
    const newItemsCheck: TableCheckItems = {};

    importTemplates.forEach(item => {
      const fileStatusCode = importFileStatuses.find(status => status.name === item.fileStatus)?.id;

      if (fileStatusCode !== ImportFileStatusEnum.PROCESSING) {
        newItemsCheck[item.id] = false;
      }
    });
    setItemsCheck(newItemsCheck);
  }, [importTemplates]);

  const handleSortClick = useCallback(
    (id: keyof IImportTemplate) => {
      const newColumnsData = handleSortChange(tableColumns, id);

      setTableColumns(newColumnsData);
      const newOrder = getSortOrder(newColumnsData);

      dispatch(importSetOrder(newOrder));
      dispatch(importGetData());
    },
    [dispatch, tableColumns],
  );

  const handleLoadButtonClick = useCallback(() => dispatch(importGetDataNextPage()), [dispatch]);

  const deletingTemplatesNumber = deletingTemplatesIds?.length || 0;

  const deletingQuantityText = num2str(deletingTemplatesNumber, ['запись', 'записи', 'записей']);

  const handleDeleteModalApply = useCallback(() => {
    if (deletingTemplatesIds) {
      dispatch(importRemove(deletingTemplatesIds));
    }
  }, [deletingTemplatesIds, dispatch]);

  const handleDeleteModalClose = useCallback(() => {
    setDeletingTemplatesIds(undefined);
    setItemsCheck(prev => {
      const newItemsCheck: TableCheckItems = {};

      Object.keys(prev).forEach(key => {
        newItemsCheck[key] = false;
      });

      return newItemsCheck;
    });
  }, []);

  const getFileTypes = () => {
    switch (role?.sysname) {
      case UserRoleEnum.SUPERIOR_PROSECUTOR:
      case UserRoleEnum.PROSECUTOR:
        return isErknm ? importFileTypesProcErknm : importFileTypesProc;
      case UserRoleEnum.SUPERVISOR:
        return isErknm
          ? (isInternalAccount && importFileTypesTestExecErknm) || importFileTypesExecErknm
          : importFileTypesExec;
      default:
        return [];
    }
  };

  const renderDropdown = useCallback(
    (data: IImportRow) => <ImportDropdown onDelete={handleImportTableRowDelete} data={data} />,
    [handleImportTableRowDelete],
  );

  return (
    <TablePage>
      <ImportPanel
        onFiltersApply={handleFiltersApply}
        dataCount={totalCount || 0}
        fileTypes={getFileTypes()}
        statuses={importFileStatuses}
        isDeleteButtonEnabled={isDeleteButtonEnabled}
        onDelete={handleImportPanelDelete}
      />
      <Table<IImportRow>
        data={importTemplates}
        columns={tableColumns}
        itemsCheck={itemsCheck}
        setItemsCheck={setItemsCheck}
        handleLoadClick={handleLoadButtonClick}
        totalCount={totalCount}
        onSortChange={handleSortClick}
        columnStyles={columnStyles}
        rowClassNames={rowClassNames}
        renderDropdown={renderDropdown}
      />
      <ConfirmModal
        visible={deletingTemplatesNumber > 0}
        title="Подтверждение"
        text={`Вы выбрали ${deletingTemplatesNumber} ${deletingQuantityText} для удаления. Удалить?`}
        applyButtonText="Удалить"
        cancelButtonText="Отменить"
        onApply={handleDeleteModalApply}
        onClose={handleDeleteModalClose}
      />
    </TablePage>
  );
};

const mapStateToProps = (state: IRootState): IImportStoreProps => ({
  role: getUserRole(state),
  isInternalAccount: getUserAccountTypeIsInternal(state),
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
  totalCount: getImportTotalCount(state),
  importTemplates: getImportData(state),
});

export const Import = connect(mapStateToProps)(ImportWithoutStore);
